import type {
  QuestionPanelModelData,
  SurveyAnswerRecord,
  SurveyAnswerValue,
  SurveyElementJson,
  SurveyPanelAnswerValue,
} from '@coorum-research/shared-common/types'
import type { SurveyModel } from 'survey-core'
import type { SurveyElementFormHandler } from './survey-element-form'
import { ValidationErrorMessages } from '@coorum-research/shared-common/shared/survey/survey-validation'
import { SurveyElementFormHandlerFile } from './survey-element-form-handler-file'
import { SurveyElementFormHandlerRating } from './survey-element-form-handler-rating'
import { isTextOrCommentElement, validateTextOrCommentElement } from './survey-element-form-handler-text'

export class SurveyElementFormHandlerPanel implements SurveyElementFormHandler {
  readonly #elementJson: SurveyElementJson
  #elementResultRecord: SurveyAnswerRecord | null
  #isUploading: boolean = false

  constructor(
    elementJson: SurveyElementJson,
    elementResultRecord: SurveyAnswerRecord | null,
  ) {
    this.#elementJson = elementJson as QuestionPanelModelData
    this.#elementResultRecord = elementResultRecord
  }

  get elementJson(): SurveyElementJson {
    return this.#elementJson
  }

  get surveyModel(): SurveyModel | null {
    return null
  }

  get data(): SurveyAnswerRecord {
    return this.#elementResultRecord || {}
  }

  getValue(): SurveyAnswerValue {
    return this.#elementResultRecord?.[this.#elementJson.name] ?? ''
  }

  getComment(): string {
    return ''
  }

  setElementResultRecordValue(value: SurveyAnswerValue) {
    if (!this.#elementResultRecord) {
      this.#elementResultRecord = {}
    }
    this.#elementResultRecord[this.#elementJson.name] = value
  }

  setElementResultRecordComment(_comment: string) {
    // nothing to do
  }

  setUploadingState(isUploading: boolean): void {
    this.#isUploading = isUploading
  }

  getUploadingState(): boolean {
    return this.#isUploading
  }

  validate(): {
    isValid: boolean
    errorMessage?: string
  } {
    // アップロード中の場合はエラー
    if (this.getUploadingState()) {
      return {
        isValid: false,
        errorMessage: ValidationErrorMessages.UPLOADING as string,
      }
    }

    const panelElement = this.#elementJson as QuestionPanelModelData
    if (!panelElement.elements) {
      return { isValid: true }
    }

    // パネル内の各要素のバリデーションを実行
    const panelValues = this.#elementResultRecord?.[this.#elementJson.name] as SurveyPanelAnswerValue
    for (const element of panelElement.elements) {
      if (isTextOrCommentElement(element)) {
        const value = panelValues?.[element.name]
        if (typeof value !== 'string') {
          continue // 文字列以外の値（画像など）はスキップ
        }
        const validationResult = validateTextOrCommentElement(element, value)
        if (!validationResult.isValid) {
          return validationResult
        }
      } else if (element.type === 'file') {
        const fileHandler = new SurveyElementFormHandlerFile(element, { [element.name]: panelValues?.[element.name] })
        const validationResult = fileHandler.validate()
        if (!validationResult.isValid) {
          return validationResult
        }
      } else if (element.type === 'rating') {
        const ratingHandler = new SurveyElementFormHandlerRating(element, { [element.name]: panelValues?.[element.name] })
        const validationResult = ratingHandler.validate()
        if (!validationResult.isValid) {
          return validationResult
        }
      }
    }

    return { isValid: true }
  }
}
